import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { cn } from './cn';
import { EmptyState } from './EmptyState';

const SEARCH_DEBOUNCE_MS = 300;

export interface DropdownOption {
  id: string;
  label: string;
  sublabel?: string;
}

export interface DropdownSelectProps {
  value: string;
  onChange: (id: string) => void;
  /** Current page of options from the server. */
  options: DropdownOption[];
  /**
   * Total number of items across all pages.
   * Omit (or set equal to options.length) to suppress pagination footer.
   */
  total?: number;
  /** Current 1-indexed page number, controlled by parent. Default: 1. */
  page?: number;
  /** Items per page — used to compute "X–Y of Z" label. Default: 20. */
  pageSize?: number;
  /** Called when the user clicks prev/next. Required when total > pageSize. */
  onPageChange?: (page: number) => void;
  /**
   * Called ~300 ms after the last keystroke with the trimmed search term.
   * When omitted, the search box still renders but no callback fires.
   */
  onSearch?: (term: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  emptyText?: string;
  className?: string;
  /** Open the dropdown on mount — used when a filter is added on demand. */
  defaultOpen?: boolean;
  /** When set, a small × replaces the chevron inside the box and calls this (removes an on-demand filter). */
  onRemove?: () => void;
  removeLabel?: string;
  /** Show the ▾ chevron (default). Filter-bar dropdowns hide it — the whole box is the click target. */
  showChevron?: boolean;
}

export function DropdownSelect({
  value,
  onChange,
  options,
  total: totalProp,
  page = 1,
  pageSize = 20,
  onPageChange,
  onSearch,
  isLoading = false,
  disabled = false,
  placeholder = '— Select —',
  emptyText = 'No options found',
  className,
  defaultOpen = false,
  onRemove,
  removeLabel = 'Remove filter',
  showChevron = true,
}: DropdownSelectProps) {
  const [open, setOpen]     = useState(false);
  const [pos, setPos]       = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);
  const [search, setSearch] = useState('');

  const triggerRef  = useRef<HTMLButtonElement>(null);
  const popupRef    = useRef<HTMLDivElement>(null);
  const searchRef   = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selected    = options.find(o => o.id === value) ?? null;
  const total       = totalProp ?? options.length;
  const totalPages  = Math.max(1, Math.ceil(total / pageSize));
  const showPaging  = total > pageSize;

  // Debounced search → parent
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch?.(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Estimated max dropdown height: search bar + options list + pagination
  const DROPDOWN_EST_H = 280;

  const openDropdown = () => {
    if (disabled || !triggerRef.current) return;
    if (open) { setOpen(false); return; }
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    const spaceAbove = rect.top - 4;
    const openAbove = spaceBelow < DROPDOWN_EST_H && spaceAbove > spaceBelow;
    setPos(
      openAbove
        ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width }
        : { top: rect.bottom + 4, left: rect.left, width: rect.width },
    );
    setSearch('');
    setOpen(true);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  // Open once on mount when requested — after mount so the trigger can be measured.
  useEffect(() => {
    if (defaultOpen) openDropdown();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // Use capture phase for scroll so we catch scrolls on any ancestor element
    // (scroll doesn't bubble). Filter out scrolls inside the popup itself so
    // the options list remains scrollable while the dropdown is open.
    const handleScroll = (e: Event) => {
      if (popupRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const triggerLabel = selected
    ? selected.sublabel
      ? `${selected.label} — ${selected.sublabel}`
      : selected.label
    : isLoading && value
      ? 'Loading…'
      : placeholder;

  const handlePageChange = (p: number) => {
    const clamped = Math.max(1, Math.min(p, totalPages));
    onPageChange?.(clamped);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border border-border-default bg-surface-1 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-border-focus/40 disabled:opacity-50',
          selected ? 'text-text-primary' : 'text-text-tertiary',
          className,
        )}
      >
        <span className="min-w-0 truncate" title={triggerLabel}>{triggerLabel}</span>
        {onRemove ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={removeLabel}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onRemove(); } }}
            className="ml-2 flex h-4 w-4 shrink-0 items-center justify-center rounded text-text-tertiary hover:bg-surface-2 hover:text-text-primary"
          >
            <X className="h-3 w-3" />
          </span>
        ) : showChevron ? (
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-text-tertiary" />
        ) : null}
      </button>

      {open && pos && (
        <div
          ref={popupRef}
          className="fixed z-[9999] overflow-hidden rounded-md border border-border-default bg-surface-0"
          style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width }}
          onMouseDown={e => e.stopPropagation()}
          onWheel={e => e.stopPropagation()}
        >
          {/* Search bar */}
          <div className="border-b border-border-subtle p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search…"
                className="h-8 w-full rounded border border-border-subtle bg-surface-1 pl-8 pr-3 text-xs text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-border-focus/40"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="flex w-full items-center px-3 py-2 text-left text-xs text-text-tertiary hover:bg-surface-1"
            >
              {placeholder}
            </button>

            {isLoading ? (
              <div className="space-y-1 p-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-surface-2" />
                ))}
              </div>
            ) : options.length === 0 ? (
              <EmptyState size="inline" title={emptyText} />
            ) : (
              options.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { onChange(opt.id); setOpen(false); }}
                  className={cn(
                    'flex w-full flex-col items-start px-3 py-2 text-left transition-colors hover:bg-surface-1',
                    opt.id === value ? 'bg-accent/10' : '',
                  )}
                >
                  <span className={cn(
                    'text-xs leading-tight',
                    opt.id === value ? 'font-medium text-accent' : 'text-text-primary',
                  )}>
                    {opt.label}
                  </span>
                  {opt.sublabel && (
                    <span className="mt-px font-mono text-[10px] text-text-tertiary">{opt.sublabel}</span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Pagination footer — only shown when total exceeds pageSize */}
          {showPaging && (
            <div className="flex items-center justify-between border-t border-border-subtle bg-surface-1 px-3 py-1.5">
              <span className="text-[10px] tabular-nums text-text-tertiary">
                {total === 0
                  ? 'No results'
                  : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  disabled={page <= 1 || isLoading}
                  onClick={e => { e.stopPropagation(); handlePageChange(page - 1); }}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded transition-colors',
                    page <= 1 || isLoading
                      ? 'cursor-not-allowed opacity-30 text-text-tertiary'
                      : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
                  )}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2.5rem] text-center text-[10px] tabular-nums text-text-tertiary">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages || isLoading}
                  onClick={e => { e.stopPropagation(); handlePageChange(page + 1); }}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded transition-colors',
                    page >= totalPages || isLoading
                      ? 'cursor-not-allowed opacity-30 text-text-tertiary'
                      : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
                  )}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
